import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { authService } from '../services/auth';
//import config from '../config';

const AppContext = createContext({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  token: null,
  notifications: [],
  login: async () => {},
  logout: async () => {},
  checkAuth: async () => {},
  addNotification: () => {},
  setUser: () => {},
  setIsAuthenticated: () => {},
  setToken: () => {}
});

let isCheckingAuth = false;
let lastAuthCheckTime = 0;
const AUTH_CHECK_THROTTLE = 2000; // Minimum 2 seconds between auth checks

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('auth_token'));
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));

  // Define checkAuth first before using it in useEffect or login
  const checkAuth = useCallback(async () => {
    // Prevent concurrent auth checks
    if (isCheckingAuth) {
      console.log('[AppContext] Auth check already in progress, skipping');
      return;
    }
    
    // Throttle auth checks to prevent rapid successive calls
    const now = Date.now();
    if (now - lastAuthCheckTime < AUTH_CHECK_THROTTLE) {
      console.log('[AppContext] Auth check throttled, last check was', now - lastAuthCheckTime, 'ms ago');
      return;
    }
    
    isCheckingAuth = true;
    lastAuthCheckTime = now;

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        throw new Error('No auth token found');
      }

      // Use authService.verifyAuth() instead of direct fetch to avoid duplication
      // This delegates to the same service used by AuthProvider
      const data = await authService.verifyAuth();
      
      if (data.valid || data.isAuthenticated) {
        setIsAuthenticated(true);
        setUser(data.user);
        return true;
      }

      throw new Error('Not authenticated');

    } catch (error) {
      console.error('[AppContext] Auth check failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      // Don't remove token on network errors
      if (!error.message?.includes('Network error')) {
        localStorage.removeItem('auth_token');
      }
      throw error;
    } finally {
      setTimeout(() => {
        isCheckingAuth = false;
      }, 1000);
    }
  }, []);

    // Then define other functions that depend on checkAuth
  const login = useCallback(async (authData) => {
    try {
      if (!authData.token) {
        throw new Error('No token provided');
      }

      // Store token first
      localStorage.setItem('auth_token', authData.token);
      
      // Then update state
      setIsAuthenticated(true);
      if (authData.user) {
        const userStr = typeof authData.user === 'string' 
          ? authData.user 
          : JSON.stringify(authData.user);
        localStorage.setItem('user', userStr);
        setUser(authData.user);
      }
      
      // Set token in state
      setToken(authData.token);
      
    } catch (error) {
      console.error('Login failed:', error);
      // Clear everything on failure
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }, []);

  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { ...notification, id }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  useEffect(() => {
    const checkInitialAuth = async () => {
        // Capture token passed via redirect query params (one-time)
        try {
           const params = new URLSearchParams(window.location.search);
           // Handle Composio OAuth permission denial flag
           const authError = params.get('auth_error');
           if (authError === 'permissions_required') {
             // Use both toast and our notification system for consistency
             toast.error('Please approve all requested Gmail permissions to continue.');
             addNotification({
               type: 'error',
               message: 'Please approve all requested Gmail permissions to continue.'
             });
           }
          const urlToken = params.get('token');
          if (urlToken) {
            localStorage.setItem('auth_token', urlToken);
            const userParam = params.get('user');
            if (userParam) {
              try {
                localStorage.setItem('user', decodeURIComponent(userParam));
              } catch (decodeError) {
                  // Ignore user decoding errors; non-critical for token persistence
                }
            }
            // Clean the URL so params don't hang around
            window.history.replaceState({}, '', window.location.pathname + window.location.hash);
          }
        } catch (e) {
          console.error('Error persisting token from URL:', e);
        }
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          console.log('[AppContext] No token found, skipping auth check');
          setIsAuthenticated(false);
          setUser(null);
          setIsLoading(false);
          return;
        }

        // Skip auth check if AuthProvider is present
        // AuthProvider will handle the verification
        const authProviderActive = window.__AUTH_PROVIDER_ACTIVE__;
        if (authProviderActive) {
          console.log('[AppContext] AuthProvider is active, skipping duplicate auth check');
          setIsLoading(false);
          return;
        }

        await checkAuth();
      } catch (err) {
        console.error('[AppContext] Initial auth check failed:', err);
        setIsAuthenticated(false);
        setUser(null);
        if (!err.message?.includes('Network error')) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkInitialAuth();
  }, [checkAuth]);

  useEffect(() => {
    setIsAuthenticated(!!user);
  }, [user]);

  if (isLoading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <AppContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      token,
      notifications,
      login,
      logout,
      checkAuth,
      addNotification,
      setUser,
      setIsAuthenticated,
      setToken
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within an AppProvider');
    }
    
    // Add auth from context
    const { isAuthenticated, token } = context;
    
    return {
        ...context,
        isAuthenticated,
        token
    };
}; 